import { measureButtonEditorWidth } from "./C1_buttonEditorWidth";
import { C1_ButtonEditorViewport } from "./C1_ButtonEditorViewport";
import { C1_Button3DPreview } from "../3DView/C1_Button3DPreview";
import { buttonUiTemplate } from "../frontViews/buttonUiTemplate";
import { itx } from "../../../src/translationItx/itx";
import { useLayoutEffect, useRef, type MouseEvent } from "react";
import { useChildConnectionEndpoints } from "../../../src/connectionAnalyser/useChildConnectionEndpoints";
import { useEffect, useState } from "react";
import Button from "../../../src/models/Button";
import type BaseModel from "../../../src/models/BaseModel";
import type ButtonChannel from "../../../src/models/ButtonChannel";
import Consumer from "../../../src/models/Consumer";
import { classifyConsumerDevice } from "../../../src/models/ConsumerDeviceKind";
import { assessConnection } from "../../../src/models/connectionCreation";
import Room from "../../../src/models/Room";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { Hand, Plus } from "lucide-react";
import { ChannelType, channelTypeInfo, getChannelTypePicture } from "../../../src/models/ChannelType";
import { OrientedButtonFrontView } from "../frontViews/ButtonFrontview";
import type { RockerRowConfig } from "../frontViews/GlasButtonFrontView";
import { C1_Button } from "../model/C1_Button";
import { DisplayNameUsage } from "../../../src/models/BaseObject";
import c1_state from "../model/C1_State";
import { C1_EventKey } from "../C1_EventKey";
import { EstadaRadioGroup } from "../../../src/components/EstadaRadioGroup";
import { C1_PaletteChapter } from "./C1_CreationPalette";
import {
  C1_INTERACTION_CONSUMER_DRAG_TYPE,
  isAssignableInteractionConsumer,
} from "../interaction/C1_interactionConsumerDrag";

/** Returns the compact semantic name shared by button assignment controls. */
export function consumerButtonDisplayName(consumer: Consumer, relativeRoom?: Room): string {
  return consumer.getDisplayName({ usage: DisplayNameUsage.ButtonEditor, relativeRoom })
    || itx("c1.ui.Unbenannter_Consumer", "Unbenannter Consumer");
}

/** Converts persisted button areas into the shared front-view data. */
function buttonRows(item: C1_Button, page = 0, press = 0): RockerRowConfig[] {
  const rows: RockerRowConfig[] = [];
  for (let rowIndex = 0; rowIndex < (item.owner?.baseProps.buttonBaseProps?.numberOfButtonRows ?? 1); rowIndex += 1) {
    const displayFunction = (buttonIndex: number) => {
      const base = item.owner?.getButtonChannel(rowIndex + page * (item.owner.baseProps.buttonBaseProps?.numberOfButtonRows ?? 1), buttonIndex);
      const estadaChannel = press ? item.owner?.buttonChannels.getItemByTag((base?.tag ?? "") + (press === 1 ? "_l" : "_xl")) : base;
      const channelType = estadaChannel?.channelType ?? ChannelType.Unknown;
      const labelsOnKeys = !item.owner?.baseProps.buttonBaseProps?.hasDisplay;
      return { channelType, name: (labelsOnKeys ? estadaChannel?.nameOrConnectedNames : estadaChannel?.name) ?? "" };
    };
    if (!item.owner?.isButtonRowSplit(rowIndex + page * (item.owner.baseProps.buttonBaseProps?.numberOfButtonRows ?? 1))) rows.push({ mode: "shared", shared: displayFunction(0) });
    else {
      rows.push({
        mode: "split",
        left: displayFunction(0),
        right: displayFunction(1),
      });
    }
  }
  return rows;
}

/** Resolves both C1-specific and standard ESTADA tree drag payloads to a consumer. */
export function draggedConsumer(dataTransfer: DataTransfer): Consumer | undefined {
  const directId = dataTransfer.getData(C1_INTERACTION_CONSUMER_DRAG_TYPE);
  if (directId) {
    const directConsumer = state.objectRegistry.getObject<Consumer>(directId);
    if (directConsumer instanceof Consumer) return directConsumer;
  }

  const serialized = dataTransfer.getData("text/json") || state.dragData;
  if (!serialized) return undefined;
  try {
    const data = JSON.parse(serialized) as { className?: string; id?: string };
    if (!data.id) return undefined;
    const consumer = state.objectRegistry.getObject<Consumer>(data.id);
    return consumer instanceof Consumer ? consumer : undefined;
  } catch {
    return undefined;
  }
}

/** Checks protected drag metadata without requiring payload access before drop. */
export function hasConsumerDragType(dataTransfer: DataTransfer): boolean {
  if (dataTransfer.types.includes(C1_INTERACTION_CONSUMER_DRAG_TYPE)) return true;
  if (dataTransfer.types.includes("text/json")) return draggedConsumer(dataTransfer) !== undefined;
  return draggedConsumer(dataTransfer) !== undefined;
}

/** Checks whether a drag-leave event still points inside the owning control. */
function dragPositionIsInside(element: HTMLElement, event: React.DragEvent): boolean {
  const rect = element.getBoundingClientRect();
  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
}

type ConsumerDragGroup = {
  id: string;
  title: string;
  consumers: Consumer[];
};

/** Selects the requested assignment group without treating switchable sockets as lights. */
function consumerDragGroup(consumer: Consumer, groups: ConsumerDragGroup[]): ConsumerDragGroup {
  if (classifyConsumerDevice(consumer).kind === "socket") return groups[1];
  const typeInfo = channelTypeInfo(consumer.channelType);
  if (typeInfo.isRollo || typeInfo.isHeating) return groups[2];
  if (typeInfo.isLight) return groups[0];
  return groups[3];
}

/** A selected occupied key is represented by its connection in the shared selection. */
export function selectedButtonAssignmentChannel(button: Button, selected: BaseModel | undefined): ButtonChannel | undefined {
  if (!selected || selected.isDeleted) return;
  for (const channel of button.buttonChannels) {
    if (channel.isDeleted) continue;
    if (channel === selected || channel.connections.asArray().some(connection => connection === selected && !connection.isDeleted)) return channel;
  }
}

/** Click assignment and dropping onto a key share validation, model updates and undo completion. */
export function assignButtonEditorConsumer(button: Button, channel: ButtonChannel, consumer: Consumer, method: "click" | "dragAndDrop"): boolean {
  if (state.projectMetadata?.readOnly || button.isDeleted || channel.isDeleted || consumer.isDeleted
    || channel.parentDevice !== button || !isAssignableInteractionConsumer(consumer)
    || !assessConnection(channel, consumer).allowed) return false;
  button.assignConsumer(channel.tag, consumer, consumer.getDisplayNameParts().function);
  c1_state.eventLogger.addEvent(C1_EventKey.ConnectionCreated, {
    buttonId: button.id, channelTag: channel.tag, channelType: channel.channelType,
    consumerId: consumer.id, method, source: "buttonEditor",
  });
  state.undoManager.userInteractionEnded();
  return true;
}

/** Exposes a room consumer to the shared connection gestures or assigns it to the selected key. */
function consumerDragSourceButton(consumer: Consumer, relativeRoom?: Room, assignment?: {enabled: boolean; channelTag: string; onAssign: () => void}): React.ReactNode {
  const displayName = consumerButtonDisplayName(consumer, relativeRoom);
  const selectAndZoom = () => {
    state.selectionManager.selectedObject = consumer;
    c1_state.zoomNavigatorObject?.(consumer);
  };
  const activate = () => {
    if (assignment) { if (assignment.enabled) assignment.onAssign(); }
    else selectAndZoom();
  };
  return (
    <button
      type="button"
      key={consumer.id}
      className={`C1_buttonConsumerDragSource${assignment?.enabled ? " C1_canAssign" : ""}`}
      data-testid={`c1-room-consumer-${consumer.id}`}
      disabled={assignment ? !assignment.enabled : undefined}
      aria-label={assignment ? itx("c1.buttonEditor.assignRoomConsumer", "{{consumer}} mit Taste {{key}} verbinden", {"{{consumer}}": displayName, "{{key}}": assignment.channelTag}) : undefined}
      data-connection-interaction="prefer-control"
      draggable={false}
      data-connection-object-id={consumer.id}
      data-room-consumer-connection="true"
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        activate();
      }}
    >
      <span className="C1_buttonConsumerDragSourceTag">{consumer.tag || "–"}</span>
      <span className="C1_buttonConsumerDragSourceName">{displayName}</span>
      {assignment && <span className="C1_buttonConsumerDragSourceAdd" aria-hidden="true"><Plus /></span>}
    </button>
  );
}

/** Lists assignable room consumers as lower-priority connection endpoints. */
function C1_ButtonConsumerDragSources({ button }: { button: Button }) {
  const endpointsElement = useRef<HTMLElement>(null);
  useChildConnectionEndpoints(state.connectionAnalyser, endpointsElement, "room-consumers");
  const [room, setRoom] = useState<Room | undefined>(() => button.parentRoom);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    setRoom(button.parentRoom);
  }, [button]);

  useOnChangeWatcher(state.selectionManager, () => {
    const selectedObject = state.selectionManager.selectedObject;
    if (selectedObject instanceof Room) setRoom(selectedObject);
    return true;
  });
  const selectedChannel = selectedButtonAssignmentChannel(button, state.selectionManager.selectedObject);

  const groups: ConsumerDragGroup[] = [
    { id: "lights", title: itx("components.plural.Light", "Leuchten"), consumers: [] },
    { id: "outlets", title: itx("components.plural.Outlet", "Steckdosen"), consumers: [] },
    { id: "shading-heating", title: itx("c1.ui.Jalousien_Rollos_Heizung", "Jalousien, Rollos & Heizung"), consumers: [] },
    { id: "other", title: itx("c1.ui.Weitere", "Weitere"), consumers: [] },
  ];
  const connectedConsumerIds = new Set<string>();
  for (const channel of button.buttonChannels) {
    for (const connection of channel.connections) {
      const partner = connection.getPartner(channel);
      if (partner instanceof Consumer) connectedConsumerIds.add(partner.id);
    }
  }
  if (room) {
    for (const consumer of room.allConsumers()) {
      if (consumer.isSubDistribution || consumer.channelType === ChannelType.IntermediateDistribution) continue;
      if (!isAssignableInteractionConsumer(consumer)) continue;
      if (connectedConsumerIds.has(consumer.id)) continue;
      consumerDragGroup(consumer, groups).consumers.push(consumer);
    }
  }

  const groupSections: React.ReactNode[] = [];
  for (const group of groups) {
    if (group.consumers.length === 0) continue;
    group.consumers.sort((left, right) => {
      const leftName = consumerButtonDisplayName(left, room);
      const rightName = consumerButtonDisplayName(right, room);
      const nameOrder = leftName.localeCompare(rightName, c1_state.language, {
        sensitivity: "base",
        numeric: true,
      });
      return nameOrder || left.tag.localeCompare(right.tag, "de", { sensitivity: "base", numeric: true });
    });
    const buttons: React.ReactNode[] = [];
    for (const consumer of group.consumers) {
      buttons.push(consumerDragSourceButton(consumer, room, selectedChannel ? {
        enabled: !state.projectMetadata?.readOnly && assessConnection(selectedChannel, consumer).allowed,
        channelTag: selectedChannel.tag,
        onAssign: () => {
          if (selectedButtonAssignmentChannel(button, state.selectionManager.selectedObject) !== selectedChannel) return;
          if (assignButtonEditorConsumer(button, selectedChannel, consumer, "click")) state.selectionManager.selectButtonEditorChannel(selectedChannel);
        },
      } : undefined));
    }
    groupSections.push(
      <C1_PaletteChapter key={group.id} id={`room-consumers-${group.id}`} title={group.title}
        layout="rows" collapsed={collapsedGroups.has(group.id)} onToggle={() => setCollapsedGroups(current => {
          const next = new Set(current);
          if (next.has(group.id)) next.delete(group.id);
          else next.add(group.id);
          return next;
        })}>
        <div className="C1_buttonConsumerDragSourceGroupItems">{buttons}</div>
      </C1_PaletteChapter>,
    );
  }

  const roomName = room?.name?.trim() || room?.tag?.trim() || "â€“";

  return (
    <section ref={endpointsElement} className="C1_buttonConsumerDragSources" aria-label={itx("c1.ui.Consumer_im_Raum", "Consumer im Raum")}>
      <div className="C1_buttonConsumerDragSourcesTitle">{itx("c1.ui.Verbraucher_im_Raum", "Verbraucher im Raum")} {roomName}</div>
      <div className="C1_buttonConsumerDragSourcesList toolbar C1_creationPalette C1_consumerChapters">
        {groupSections.length > 0
          ? groupSections
          : (
            <span className="C1_buttonConsumerDragSourcesEmpty">
              {itx("c1.ui.Alle_Verbraucher_in_value1_sind_bereits_mit_dem_Taster_verbunden", "Alle Verbraucher in {{value1}} sind bereits mit dem Taster verbunden", { "{{value1}}": String(roomName) })}
            </span>
          )}
      </div>
    </section>
  );
}

/** Selects the device on free editor surfaces while preserving channel and control clicks. */
function selectButtonEditorBackground(event: MouseEvent<HTMLDivElement>, button: Button | undefined, selection: typeof state.selectionManager): void {
  const target = event.target as Element;
  if (!button || target.closest('[data-connection-object-id], button, input, select, textarea, a, [role="button"], [contenteditable="true"]')) return;
  event.stopPropagation();
  selection.selectedObject = button;
}

/** Owns the editable button rendering and, in interaction mode, its drag-and-drop assignment surface. */
export function C1_ButtonEditorView({ item, actionsInHeader = false, onPreviewContextChange, inlinePlan = false }: {
  item: C1_Button;
  actionsInHeader?: boolean;
  inlinePlan?: boolean;
  onPreviewContextChange?: (context: { itemId: string; page: number; press: number }) => void;
}) {
  useOnChangeWatcher(c1_state.editMode);
  useOnChangeWatcher(state.selectionManager);
  useOnChangeWatcher(state.selectionManager.displaySelections);
  useOnChangeWatcher(state.mobileEditEnabled);
  const [dragActive, setDragActive] = useState(false);
  const [activeChannelTag, setActiveChannelTag] = useState<string>();
  const [page, setPage] = useState(0);
  const [press, setPress] = useState(0);
  useLayoutEffect(() => {
    onPreviewContextChange?.({ itemId: item.id, page, press });
  }, [item.id, page, press, onPreviewContextChange]);
  const patchEndpoints = useRef<HTMLDivElement>(null);
  useChildConnectionEndpoints(state.connectionAnalyser, patchEndpoints, "button-editor");
  const analyserElement = useRef<HTMLDivElement>(null);
  const channelEndpointsElement = useRef<HTMLDivElement>(null);
  useChildConnectionEndpoints(state.connectionAnalyser, channelEndpointsElement, "button-editor");
  useOnChangeWatcher(item.owner);
  useOnChangeWatcher(item.owner?.template);
  useOnChangeWatcher(item.owner?.channels);
  const properties = item.owner?.baseProps.buttonBaseProps;
  useEffect(() => { setPage(0); setPress(0); }, [item.id, properties?.numOfPages, properties?.supportsLongPress, properties?.supportsSlap]);
  // Splitting changes channels, but not the physical size. Releasing the dock width
  // during a remeasurement would temporarily shrink the viewport and clamp its pan.
  useLayoutEffect(() => {
    const root = analyserElement.current;
    const shell = root?.closest<HTMLElement>(".C1_rightDock--docked, .C1_floatingEloEditor");
    if (!root || !shell) return;
    const view = root.querySelector<HTMLElement>(".C1_buttonAssignmentView") ?? root;
    const key = shell.classList.contains("C1_rightDock--docked") ? "--c1-dock-auto-width" : "width";
    const previous = shell.style.getPropertyValue(key);
    const auxiliary = root.querySelector<HTMLElement>(".C1_buttonEditorPatch");
    const width = Math.max(measureButtonEditorWidth(view, shell), auxiliary ? measureButtonEditorWidth(auxiliary, shell) : 0);
    shell.style.setProperty(key, `${width}px`);
    return () => {
      if (previous) shell.style.setProperty(key, previous);
      else shell.style.removeProperty(key);
    };
  }, [item.id, properties?.widthMm, properties?.horizontal, properties?.numberOfButtonRows, properties?.supportsSlap, state.settings.applicationMode, page, press, c1_state.editMode.value]);
  const [frontImage, setFrontImage] = useState<string>();
  const template = item.owner?.template;
  const standaloneElectrical = state.settings?.applicationMode === "c1"
    && ["EloConcept", "EloDetail"].includes(c1_state.editMode.value);
  const useFrontImage = !!template && (standaloneElectrical
    || (template.baseProps === undefined && template.genericParams === undefined));
  useEffect(() => {
    let active = true;
    setFrontImage(undefined);
    if (useFrontImage && template) {
      const loadImage = async () => {
        await template.knxProductData.loadFrontImage();
        if (active) setFrontImage(standaloneElectrical
          ? template.knxProductData.frontImage ?? item.owner?.frontImage
          : item.owner?.frontImage);
      };
      void loadImage().catch(() => { /* Leave the image surface empty if loading fails. */ });
    }
    return () => { active = false; };
  }, [item.owner, template, useFrontImage, standaloneElectrical, template?.useGenericImage]);
  if (!properties) return null;
  const buttonSvgWidthMm = properties.widthMm * 0.5;
  const rows = buttonRows(item, page, press);
  const suffix = press === 1 ? "_l" : press === 2 ? "_xl" : "";
  const getChannel = (row: number, side: string) => {
    const base = item.owner?.getButtonChannel(row + page * properties.numberOfButtonRows, side === "right" ? 1 : 0);
    return suffix ? item.owner?.buttonChannels.getItemByTag((base?.tag ?? "") + suffix) : base;
  };
  // Electrical planning shows the physical keys; operation retains page and press choices.
  const controls = !standaloneElectrical && <div className="C1_buttonEditorChoices">
    {properties.numOfPages > 1 && <EstadaRadioGroup label={itx("c1.buttonEditor.pages", "Seite")} value={page}
      options={Array.from({length: properties.numOfPages}, (_, index) => ({value:index, label:itx("c1.buttonEditor.page", "Seite {{number}}", {"{{number}}":String(index + 1)})}))} onChange={setPage} />}
    {(properties.supportsLongPress > 0 || properties.supportsSlap === 2) && <div className="C1_buttonEditorPress"><span>{itx("c1.buttonEditor.press", "Drücken")}</span>
      <EstadaRadioGroup label={itx("c1.buttonEditor.press", "Drücken")} value={press} onChange={setPress} options={[
        {value:0,label:itx("c1.buttonEditor.normal", "normal")}, {value:1,label:itx("c1.buttonEditor.long", "lang")},
        ...(properties.supportsLongPress === 2 || properties.supportsSlap === 2 ? [{value:2,label:itx("c1.buttonEditor.extraLong", "extra lang")}] : []),
      ]} /></div>}
  </div>;
  const assignmentMode = state.mobileEditEnabled.value || (!standaloneElectrical
    && (c1_state.editMode.value === "interactionConcept" || c1_state.editMode.value === "EloDetail"));
  const sideAssignments = properties.horizontal && properties.numberOfButtonRows <= 2;
  const assignmentsAboveBelow = properties.horizontal && !sideAssignments;
  const orientationClass = assignmentsAboveBelow ? "C1_vertical" : "C1_horizontal";
  const button = item.owner instanceof Button ? item.owner : undefined;
  const showButtonLabels = !properties.hasDisplay;
  const channelGridStyle =
    properties.horizontal
      ? { gridTemplateColumns: `repeat(${properties.numberOfButtonRows}, minmax(0, 1fr))`, gridTemplateRows: "repeat(2, minmax(0, 1fr))" }
      : { gridTemplateRows: `repeat(${properties.numberOfButtonRows}, minmax(0, 1fr))` };
  const assignmentGridStyle = sideAssignments
    ? { gridTemplateRows: "repeat(2, minmax(0, 1fr))" }
    : properties.horizontal
      ? { gridTemplateColumns: `repeat(${properties.numberOfButtonRows}, minmax(0, 1fr))` }
      : channelGridStyle;

  const channelTargets: React.ReactNode[] = [];
  const firstAssignments: React.ReactNode[] = [];
  const secondAssignments: React.ReactNode[] = [];
  const patchTargets: React.ReactNode[] = [];
  const patchAssignments: React.ReactNode[] = [];
  const temperatureTargets: React.ReactNode[] = [];
  const temperatureAssignments: React.ReactNode[] = [];
  if (button) {
    for (const channel of button.buttonChannels.asArray()) {
      const patch = properties.supportsSlap > 0 && channel.tag === "TP";
      const temperature = !standaloneElectrical && channel.tag === "TX_T";
      const auxiliary = patch || temperature;
      const match = /^T(\d+)(L|R)?(_l|_xl)?$/.exec(channel.tag);
      if (!auxiliary && (!match || (match[3] ?? "") !== suffix || Number(match[1]) <= page * properties.numberOfButtonRows || Number(match[1]) > (page + 1) * properties.numberOfButtonRows)) continue;
      const rowIndex = auxiliary ? 0 : Number(match![1]) - 1 - page * properties.numberOfButtonRows;
      const shared = !match?.[2];
      const right = match?.[2] === "R";
      const consumerEntries: React.ReactNode[] = [];
      for (const connection of channel.connections.asArray()) {
        const partner = connection.getPartner(channel);
        if (!(partner instanceof Consumer)) continue;
        const partnerDisplayName = consumerButtonDisplayName(partner, button.parentRoom);
        consumerEntries.push(
          <div
            key={connection.id}
            className="C1_buttonConsumerAssignmentEntry"
            data-connection-interaction="prefer-control"
            role="button"
            tabIndex={0}
            draggable
            onDragStart={(event) => {
              const mode = event.shiftKey || event.ctrlKey ? "copy" : "copyMove";
              partner.treeViewModel.dragStart(event, mode);
            }}
            onClick={(event) => {
              event.stopPropagation();
              state.selectionManager.selectButtonEditorChannel(channel, connection);
            }}
            onDoubleClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              c1_state.zoomNavigatorObject?.(partner);
            }}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              state.selectionManager.selectButtonEditorChannel(channel, connection);
            }}
          >
            <span className="C1_buttonConsumerAssignmentTag">{partner.tag || "â€“"}</span>
            <span>{partnerDisplayName}</span>
            <button
              type="button"
              className="unlink button circle C1_buttonConsumerAssignmentDelete"
              title={itx("terminalStripPropertiesView.deleteConnectionTerminal", "Verbindung lÃ¶sen")}
              aria-label={itx("c1.ui.Verbindung_zu_value1_losen", "Verbindung zu {{value1}} lÃ¶sen", { "{{value1}}": String(partnerDisplayName || partner.tag) })}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                state.undoManager.runAsSingleStep(itx("c1.ui.Tastenzuordnung_losen", "Tastenzuordnung lÃ¶sen"), () => {
                  connection.delete();
                });
              }}
              onDoubleClick={(event) => event.stopPropagation()}
            >
              <i className="las la-unlink" />
            </button>
          </div>,
        );
      }
      if (consumerEntries.length > 0) {
        const assignmentStyle =
          sideAssignments ? { gridRow: shared ? "1 / 3" : right ? 1 : 2 }
            : properties.horizontal ? { gridColumn: rowIndex + 1 } : { gridRow: rowIndex + 1 };
        const assignment = (
          <div key={channel.id} className="C1_buttonConsumerAssignment" style={auxiliary ? undefined : assignmentStyle}>
            {consumerEntries}
          </div>
        );
        if (temperature) temperatureAssignments.push(assignment);
        else if (patch) patchAssignments.push(assignment);
        else if (sideAssignments ? rowIndex === 1 : right) secondAssignments.push(assignment);
        else firstAssignments.push(assignment);
      }
      const targetStyle =
        properties.horizontal
          ? { gridColumn: rowIndex + 1, gridRow: shared ? "1 / 3" : right ? 1 : 2 }
          : { gridRow: rowIndex + 1, gridColumn: shared ? "1 / 3" : right ? 2 : 1 };
      (temperature ? temperatureTargets : patch ? patchTargets : channelTargets).push(
        <div
          key={channel.id}
          className={`C1_buttonChannelTarget${activeChannelTag === channel.tag ? " C1_active" : ""}`}
          style={auxiliary ? undefined : targetStyle}
          data-connection-interaction={auxiliary ? "prefer-control" : undefined}
          data-connection-object-id={auxiliary ? channel.id : undefined}
          role={auxiliary ? "button" : undefined}
          tabIndex={auxiliary ? 0 : undefined}
          onKeyDown={auxiliary ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); state.selectionManager.selectButtonEditorChannel(channel); } } : undefined}
          onClick={(event) => {
            event.stopPropagation();
            state.selectionManager.selectButtonEditorChannel(channel);
          }}
          onDragEnter={(event) => {
            if (!hasConsumerDragType(event.dataTransfer)) return;
            event.preventDefault();
            event.stopPropagation();
            setActiveChannelTag(channel.tag);
          }}
          onDragOver={(event) => {
            if (!hasConsumerDragType(event.dataTransfer)) return;
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = "copy";
            if (activeChannelTag !== channel.tag) setActiveChannelTag(channel.tag);
          }}
          onDragLeave={(event) => {
            if (!dragPositionIsInside(event.currentTarget, event)) setActiveChannelTag(undefined);
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setDragActive(false);
            setActiveChannelTag(undefined);
            const consumer = draggedConsumer(event.dataTransfer);
            if (!(consumer instanceof Consumer) || !button) return;
            assignButtonEditorConsumer(button, channel, consumer, "dragAndDrop");
          }}
        >{auxiliary && <>
          {temperature
            ? <img className="C1_buttonAuxiliaryIcon" src={getChannelTypePicture(ChannelType.Heating)} alt="" draggable={false} />
            : <Hand className="C1_buttonAuxiliaryIcon" aria-hidden="true" />}
          <span className="C1_buttonAuxiliaryLabel">{temperature
            ? itx("Channel.TemperatureMeasurement", "Temperatur-Messung")
            : itx("c1.buttonEditor.slap", "Patsch")}</span>
        </>}</div>,
      );
    }
  }

  const buttonSvg = (
    <OrientedButtonFrontView
      className={useFrontImage && (!standaloneElectrical || frontImage) ? "C1_buttonFrontImage" : undefined}
      selectedButtonChannel={state.selectionManager.selectedObject}
      displayedButtonChannelIds={new Set(state.selectionManager.displaySelections.getNavigatorPath()
        .filter(object => object.isButtonChannel).map(object => object.id))}
      uiTemplate={buttonUiTemplate(item.owner?.baseProps.buttonBaseProps?.type)}
      getButtonChannelId={standaloneElectrical && !showButtonLabels && !inlinePlan ? undefined : (rowIndex, side) => getChannel(rowIndex, side)?.id}
      rows={rows}
      width={buttonSvgWidthMm}
      height={properties.heightMm * 0.5}
      color={properties.color ?? "white"}
      horizontal={properties.horizontal}
      hasDisplay={properties.hasDisplay ?? false}
      showButtonLabels={showButtonLabels}
      constrainLabels={showButtonLabels}
      showDisplayOverlay={!standaloneElectrical}
      onButtonClick={(rowIndex, side) => {
        const channel = getChannel(rowIndex, side);
        if (channel) state.selectionManager.selectButtonEditorChannel(channel);
      }}
      onNameChange={inlinePlan ? undefined : (rowIndex, target, name) => {
        const channel = getChannel(rowIndex, target);
        if (!channel) return;
        channel.name = name;
        item.owner?.raiseOnChangeUI("channels");
      }}
      onRowModeChange={properties.supportsSplitRocker && press === 0
        ? (rowIndex, mode) => item.owner?.setButtonRowSplit(rowIndex + page * properties.numberOfButtonRows, mode === "split")
        : undefined}
    />
  );

  const buttonView = useFrontImage ? (
    <div className={`C1_buttonFrontImageLayers${standaloneElectrical ? " C1_buttonFrontImageLayers--electrical" : ""}`}
      style={standaloneElectrical && frontImage ? {
        width: `${buttonSvgWidthMm}mm`, maxWidth: "min(320px, 100%)",
        aspectRatio: `${buttonSvgWidthMm} / ${properties.heightMm * 0.5}`,
      } : undefined}>
      {frontImage && <img src={frontImage} alt="" draggable={false} />}
      {(inlinePlan || !standaloneElectrical || !frontImage || showButtonLabels) && buttonSvg}
    </div>
  ) : buttonSvg;
  const patchView = !standaloneElectrical && (patchTargets.length > 0 || temperatureTargets.length > 0) && <div ref={patchEndpoints} className="C1_buttonEditorPatch">
    {patchTargets.length > 0 && <div className="C1_buttonEditorAuxiliaryChannel">{patchTargets}{/* {patchAssignments} */}</div>}
    {temperatureTargets.length > 0 && <div className="C1_buttonEditorAuxiliaryChannel">{temperatureTargets}{/* {temperatureAssignments} */}</div>}
  </div>;
  const preview3D = !actionsInHeader && button && <C1_Button3DPreview key={`preview:${item.id}`} button={button} page={page} press={press} />;

  if (!assignmentMode) return (
    <div ref={analyserElement} className={`C1_buttonFrontViewDialogPreview${standaloneElectrical ? " C1_buttonFrontViewDialogPreview--electrical" : ""}`} onClick={(event) => selectButtonEditorBackground(event, button, state.selectionManager)}>
      {controls}
      {preview3D}
      <C1_ButtonEditorViewport key={`viewport:${item.id}`} onViewportChange={() => state.connectionAnalyser.refreshEndpoints()}>
        <div ref={channelEndpointsElement}>{buttonView}</div>
        {patchView}
      </C1_ButtonEditorViewport>
    </div>
  );

  return (
    <div ref={analyserElement} className="C1_buttonInteractionEditor" onClick={(event) => selectButtonEditorBackground(event, button, state.selectionManager)}>
      {controls}
      {preview3D}
      <C1_ButtonEditorViewport key={`viewport:${item.id}`} onViewportChange={() => state.connectionAnalyser.refreshEndpoints()}>
      <div
        ref={channelEndpointsElement}
        className={`C1_buttonAssignmentView ${orientationClass}`}
        onDragEnter={(event) => {
          if (hasConsumerDragType(event.dataTransfer)) setDragActive(true);
        }}
        onDragOver={(event) => {
          if (!hasConsumerDragType(event.dataTransfer)) return;
          event.preventDefault();
          if (!dragActive) setDragActive(true);
        }}
        onDragLeave={(event) => {
          if (!dragPositionIsInside(event.currentTarget, event)) {
            setDragActive(false);
            setActiveChannelTag(undefined);
          }
        }}
      >
        {/* Verbraucherreferenzen vorläufig ausgeblendet.
        <div className="C1_buttonConsumerAssignments C1_first" style={assignmentGridStyle}>
          {assignmentsAboveBelow ? secondAssignments : firstAssignments}
        </div>
        */}
        <div className={`C1_buttonAssignmentDropSurface${dragActive ? " C1_dragActive" : ""}`}>
          {buttonView}
        </div>
        {/*
        <div className="C1_buttonConsumerAssignments C1_second" style={assignmentGridStyle}>
          {assignmentsAboveBelow ? firstAssignments : secondAssignments}
        </div>
        */}
        <div
          className={`C1_buttonChannelTargets${dragActive ? " C1_acceptingDrop" : ""}`}
          style={channelGridStyle}
        >
          {channelTargets}
        </div>
      </div>
      {patchView}
      </C1_ButtonEditorViewport>
      {button && !inlinePlan && <C1_ButtonConsumerDragSources button={button} />}
    </div>
  );
}
